import { memo } from 'react'
import { useTranslations } from '@/core/translation'
import { useDeviceType } from '@/utils'
import { FilterDropdown } from '@/components/Data'
import { Checkbox, CheckboxGroup, Radio, RadioGroup } from '@/components/Inputs'
import { Box } from '@/components/Layout'
import { BottomDrawer } from '@/components/Surface'
import { FilterChip } from '@/components/Utils'
import { filterDropdownResponsiveStyles as styles } from './FilterDropdownResponsive.style'
import { FilterDropdownResponsiveProps } from './FilterDropdownResponsive.types'
import { useFilterDropdownResponsiveController } from './FilterDropdownResponsive.controller'

function FilterDropdownResponsive<Options extends object>({
  onClear,
  hide = false,
  ...props
}: FilterDropdownResponsiveProps<Options>) {
  const { isMobile } = useDeviceType()

  if (hide) return null

  if (isMobile) return <FilterDropdownMobile onClear={onClear} {...props} />
  return <FilterDropdown {...props} />
}

function FilterDropdownMobile<Options extends object>(
  props: FilterDropdownResponsiveProps<Options>,
) {
  const { label, onClear, options, idKey, valueKey, labelKey, multiple } = props

  const t = useTranslations('components')
  const {
    displayLabel,
    hasValue,
    isDrawerOpen,
    setIsDrawerOpen,
    closeDrawer,
    onApply,
    intermediateValue,
    intermediateValueArray,
    setIntermediateValue,
    onRadioChange,
    onCheckboxChange,
  } = useFilterDropdownResponsiveController(props)

  return (
    <>
      <FilterChip
        label={displayLabel}
        isActive={hasValue}
        onClick={() => setIsDrawerOpen(true)}
        onClear={onClear}
        enableClear={!!onClear}
      />

      <BottomDrawer
        open={isDrawerOpen}
        onOpen={() => {}}
        onClose={closeDrawer}
        heading={label?.toString()}
        actionButtonProps={{
          primaryActionText: t('filters.apply'),
          onPrimaryActionClick: onApply,
          secondaryActionText: t('filters.clear'),
          onSecondaryActionClick: () => {
            setIntermediateValue(multiple ? [] : '')
          },
        }}>
        <Box sx={styles.contentWrapper}>
          {!multiple && (
            <RadioGroup title="" value={intermediateValue ?? ''} onChange={onRadioChange}>
              {options.map((option) => (
                <Radio
                  key={String(option[idKey])}
                  label={String(option[labelKey])}
                  value={option[valueKey]}
                />
              ))}
            </RadioGroup>
          )}

          {multiple && (
            <CheckboxGroup title="">
              {options.map((option) => (
                <Checkbox
                  key={String(option[idKey])}
                  label={String(option[labelKey])}
                  checked={intermediateValueArray.includes(String(option[valueKey]))}
                  name={String(option[idKey])}
                  onChange={onCheckboxChange}
                />
              ))}
            </CheckboxGroup>
          )}
        </Box>
      </BottomDrawer>
    </>
  )
}

export default memo(FilterDropdownResponsive) as unknown as typeof FilterDropdownResponsive
